<script>
import moment from "moment";
import loaderModal from "../../../components/Loaders/loaderModal.vue";
import IndividualCheckin from "@/components/Checkins/IndividualCheckin.vue";
import ListCheckinGroup from "@/components/Checkins/ListCheckinGroup.vue";
import CheckinEditDrawer from "@/components/Checkins/CheckinEditDrawer.vue";
import { formatDateUTC, calcAgeFromUTC } from "@/utils/date";

export default {
  name: "ModalDatosGeneral",
  components: {
    loaderModal,
    IndividualCheckin,
    ListCheckinGroup,
    CheckinEditDrawer,
  },
  data() {
    return {
      showDrawer: false,
      editingCheckin: null,
    };
  },
  computed: {
    property() {
      return this.$store.state.property?.property || {};
    },
    checkin() {
      return this.$store.state.datosModal || null;
    },
    associates() {
      return this.checkin?.checkin_associates || [];
    },
    isGroup() {
      return (this.associates?.length || 0) > 1;
    },
    // ── País de la propiedad del checkin (fuente de verdad) ─────────────────
    // Prioritiza la propiedad del propio checkin para no depender del store global.
    checkinPropertyCode() {
      const code = String(
        this.checkin?.propiedad?.code_pais ||
        this.property?.code_pais ||
        ""
      ).toUpperCase();
      if (code === "ESP") return "ES";
      if (code === "ITA") return "IT";
      if (code === "COL") return "CO";
      return code;
    },
    isVisibleData() {
      return this.checkinPropertyCode === "ES";
    },
    isItaly() {
      return this.checkinPropertyCode === "IT";
    },
    // ── Autoridad de registro del check-in (etiqueta + bandera) ─────────────
    // Prioriza la clave resuelta por el backend (checkin.police_authority);
    // si aún no llega, cae a una clave por país. Refleja la autoridad real
    // (SES / Ertzaintza / Mossos / Alloggiati / SIRE), no siempre "SES Hospedaje".
    authority() {
      const key =
        this.checkin?.police_authority ||
        (this.isItaly
          ? "alloggiati"
          : this.checkinPropertyCode === "CO"
          ? "sire"
          : this.isVisibleData
          ? "ses_hospedaje"
          : null);

      const MAP = {
        ses_hospedaje: { label: "SES Hospedaje",     flag: "🇪🇸" },
        ertzaintza:    { label: "Ertzaintza",        flag: "🇪🇸" },
        mossos:        { label: "Mossos d'Esquadra", flag: "🇪🇸" },
        alloggiati:    { label: "Alloggiati Web",    flag: "🇮🇹" },
        sire:          { label: "SIRE",              flag: "🇨🇴" },
      };
      return { key, ...(MAP[key] || MAP.ses_hospedaje) };
    },
    guestCount() {
      return Math.max(1, this.associates?.length || 0);
    },
    // Propiedad efectiva: merge del store + la del checkin, priorizando code_pais del checkin
    checkinProperty() {
      const storeProp   = this.property   || {};
      const checkinProp = this.checkin?.propiedad || {};
      return { ...storeProp, ...checkinProp };
    },
    reserva() {
      return this.checkin?.programmed || null;
    },
    hasReserva() {
      return !!this.reserva && !!this.reserva.id;
    },
  },
  mounted() {
    this.bindModalEvents();
  },
  beforeDestroy() {
    this.unbindModalEvents();
    this.restoreModalFocusTrap();
  },
  methods: {
    moment,
    formatDateUTC,
    calcAgeFromUTC,

    bindModalEvents() {
      const $ = window.$;
      if (!$) return;
      $("#modalDatosGeneral")
        .off("hidden.bs.modal.wk")
        .on("hidden.bs.modal.wk", () => {
          this.showDrawer = false;
          this.editingCheckin = null;
          this.restoreModalFocusTrap();
          setTimeout(() => {
            this.$store.state.datosModal = null;
          }, 300);
        });
    },

    unbindModalEvents() {
      const $ = window.$;
      if (!$) return;
      $("#modalDatosGeneral").off("hidden.bs.modal.wk");
    },

    disableModalFocusTrap() {
      const $ = window.$;
      if (!$) return;
      $(document).off("focusin.bs.modal");
    },

    restoreModalFocusTrap() {
      const $ = window.$;
      if (!$) return;
      const inst = $("#modalDatosGeneral").data("bs.modal");
      if (inst && typeof inst._enforceFocus === "function") {
        inst._enforceFocus();
      }
    },

    getPaymentLabel(payment) {
      const keys = ["DESTI", "EFECT", "MOVIL", "OTRO", "PLATF", "TARJT", "TRANS", "TREG"];
      const k = keys.indexOf(payment) !== -1 ? payment : "OTRO";
      return this.$t(`maquetaTpl.modalDatos.pay.${k}`);
    },

    safeText(value) {
      return value ? value : this.$t("maquetaTpl.modalDatos.unspecified");
    },

    diffDays(from, to) {
      return moment(to).diff(moment(from), "days");
    },

    calcAge(date) {
      return moment().diff(moment(date), "years");
    },

    functionCerrarModal() {
      const $ = window.$;
      if ($) {
        $("#modalDatosGeneral").modal("hide");
      }
    },

    handleEditCheckin(payload) {
      this.editingCheckin = payload.item || this.checkin;
      this.disableModalFocusTrap();
      this.showDrawer = true;
    },

    handleCloseDrawer() {
      this.showDrawer = false;
      this.editingCheckin = null;
      this.restoreModalFocusTrap();
    },

    handleSavedCheckin(updated) {
      if (this.$store.state.datosModal) {
        if (this.$store.state.datosModal.id === updated.id) {
          Object.assign(this.$store.state.datosModal, updated);
          if (updated.cliente && this.$store.state.datosModal.cliente) {
            Object.assign(this.$store.state.datosModal.cliente, updated.cliente);
          }
        } else {
          const index = this.$store.state.datosModal.checkin_associates?.findIndex(
            (a) => a.id === updated.id
          );
          if (index !== -1 && index !== undefined) {
            Object.assign(this.$store.state.datosModal.checkin_associates[index], updated);
          }
        }
      }
      this.handleCloseDrawer();
    },

    emitRemove(payload) {
      this.$emit("remove-checkin-confirme", payload);
    },

    goBooking() {
      if (!this.hasReserva) return;
      const $ = window.$;
      if (!$) {
        this.$router.push({
          path: `scheduled-checkins/${this.reserva.user_id}/${this.reserva.id}/booking`,
        });
        return;
      }
      $("#modalDatosGeneral").modal("hide");
      $("#modalDatosGeneral").one("hidden.bs.modal", () => {
        this.$router.push({
          path: `scheduled-checkins/${this.reserva.user_id}/${this.reserva.id}/booking`,
        });
      });
    },
  },
};
</script>

<template>
  <div
    class="modal fade"
    id="modalDatosGeneral"
    style="text-align: left"
    data-backdrop="static"
    data-keyboard="false"
    tabindex="-1"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div
        class="modal-content wk-modal"
        :class="{ 'wk-modal--mobile': $isMobile }"
      >
        <loaderModal v-if="$store.state.efecto" />

        <!-- HEADER -->
        <div class="wk-header" v-if="!$store.state.efecto">
          <div class="wk-header__left">
            <div class="wk-title">{{ $t("maquetaTpl.modalDatos.regTitle") }}</div>

            <div class="wk-subtitle" v-if="checkin">
              <div class="wk-subtitle__row">
                <span class="wk-subtitle__name">{{ checkin?.propiedad?.nombre }}</span>
              </div>
              <div class="wk-subtitle__row">
                <template v-if="hasReserva">
                  <button
                    type="button"
                    class="wk-subtitle__link"
                    @click.prevent="goBooking"
                  >
                    Reserva #{{ reserva.codigo_reserva }}
                  </button>
                  <span class="wk-dot">•</span>
                </template>
                <span class="wk-subtitle__muted">
                  Fecha de la solicitud:
                  {{ moment(checkin.created_at).format("DD/MM/YYYY") }}
                  a las
                  {{ moment(checkin.created_at).format("hh:mm A") }}
                </span>
              </div>
            </div>
          </div>

          <button
            type="button"
            class="wk-close"
            data-dismiss="modal"
            aria-label="Close"
            @click.prevent="functionCerrarModal()"
          >
            <i class="fa-regular fa-times"></i>
          </button>
        </div>

        <div class="wk-divider" v-if="!$store.state.efecto"></div>

        <!-- BODY -->
        <div class="wk-body" v-if="checkin && !$store.state.efecto">
          <!-- Resumen -->
          <div class="wk-card wk-card--soft">
            <div class="wk-kv">
              <div class="wk-kv__item">
                <div class="wk-kv__label">{{ $t("maquetaTpl.modalDatos.paymentMethod") }}</div>
                <div class="wk-kv__value wk-kv__value--primary">
                  {{ getPaymentLabel(checkin.type_payment_checkin) }}
                </div>
              </div>

              <div class="wk-kv__item" v-if="isVisibleData">
                <div class="wk-kv__label">{{ $t("maquetaTpl.modalDatos.paymentDate") }}</div>
                <div class="wk-kv__value wk-kv__value--primary">
                  {{ moment(checkin.payment_date_checkin).format("DD/MM/YYYY") }}
                </div>
              </div>

              <div class="wk-kv__item">
                <div class="wk-kv__label">{{ $t("maquetaTpl.modalDatos.guestCount") }}</div>
                <div class="wk-kv__value">{{ guestCount }}</div>
              </div>
            </div>
          </div>

          <div class="wk-section-space"></div>

          <!-- Individual / Grupo -->
          <individual-checkin
            v-if="!isGroup"
            :mostrar="checkin"
            :dias="diffDays"
            :edad="calcAge"
            :property="checkinProperty"
            :isVisibleData="isVisibleData"
            :isItaly="isItaly"
            :authority="authority"
            :validateField="safeText"
            @edit-checkin="handleEditCheckin"
          />

          <list-checkin-group
            v-else
            :associates="associates"
            :edad="calcAge"
            :property="checkinProperty"
            :isVisibleData="isVisibleData"
            :isItaly="isItaly"
            :authority="authority"
            :validateField="safeText"
            @edit-checkin="handleEditCheckin"
            @remove-checkin="emitRemove"
          />
        </div>

        <!-- DRAWER -->
        <checkin-edit-drawer
          :visible="showDrawer"
          :historial="editingCheckin"
          :property="checkinProperty"
          :isVisibleData="isVisibleData"
          @close="handleCloseDrawer"
          @saved="handleSavedCheckin"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.wk-modal {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 18px 18px 14px;
}
.wk-modal--mobile {
  padding: 12px;
}

.wk-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.wk-title {
  font-size: 18px;
  font-weight: 700;
  color: #111827;
}

.wk-subtitle {
  margin-top: 6px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.wk-subtitle__row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  font-size: 13px;
  color: #6b7280;
  font-weight: 600;
}

.wk-subtitle__name {
  color: #111827;
  font-weight: 700;
}
.wk-subtitle__muted {
  color: #6b7280;
  font-weight: 600;
}
.wk-dot {
  color: #cbd5e1;
}

.wk-subtitle__link {
  color: #2563eb;
  font-weight: 700;
  font-size: 13px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  text-decoration: none;
  transition: color 0.2s ease;

  &:hover {
    color: #1d4ed8;
    text-decoration: underline;
  }

  &:focus {
    outline: 2px solid #2563eb;
    outline-offset: 2px;
    border-radius: 4px;
  }
}

.wk-close {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  color: #111827;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.wk-divider {
  height: 1px;
  background: #eef2f7;
  margin: 14px 0;
}

.wk-body {
  padding: 0;
}

.wk-card {
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  background: #ffffff;
  padding: 14px;
}
.wk-card--soft {
  background: #f8fafc;
  border-color: #e6edf7;
}

.wk-kv {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.wk-kv__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
}
.wk-kv__label {
  color: #6b7280;
  font-size: 13px;
  font-weight: 600;
}
.wk-kv__value {
  color: #111827;
  font-size: 13px;
  font-weight: 700;
}
.wk-kv__value--primary {
  color: #2563eb;
}

.wk-section-space {
  height: 14px;
}
</style>
